/*
 * Copyright (c) 2022 Well Solutions SRL. All Rights Reserved.
 * This software is the proprietary information of Well Solutions SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from '@angular/core';
import { RoleResourceService } from './role-resource.service';
import { RoleWithRightsDto } from './models/role-with-rights-dto.model';
import { RoleWithRightsVo } from './models/role-with-rights-vo.model';
import { Observable } from 'rxjs';
import { JsonApiResource } from '@irian-ro/abstract-resource';
import { AbstractRoleService } from './abstract-role.service';

@Injectable({ providedIn: 'root' })
export class RoleService implements AbstractRoleService {
  constructor(private readonly roleResourceService: RoleResourceService) {}

  findOne(uuid: string): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.roleResourceService.findOne(uuid);
  }

  findAll(): Observable<JsonApiResource<RoleWithRightsVo[], any>> {
    return this.roleResourceService.findAll();
  }

  save(
    roleWithRightsDto: RoleWithRightsDto
  ): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.roleResourceService.save(roleWithRightsDto);
  }

  remove(uuid: string): Observable<void> {
    return this.roleResourceService.remove(uuid);
  }

  update(
    roleWithRightsDto: RoleWithRightsDto
  ): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.roleResourceService.update(roleWithRightsDto);
  }

  getNewTemplate(): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.roleResourceService.getTemplate();
  }

  getNewTemplateFromOrigin(
    originalRoleUuid: string
  ): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    return this.roleResourceService.getTemplateFromOrigin(originalRoleUuid);
  }

  findRolesByUserUuid(
    userUuid: string
  ): Observable<JsonApiResource<RoleWithRightsVo[], any>> {
    return this.roleResourceService.findRolesByUserUuid(userUuid);
  }

  getEditedRoleDto(
    operation: string,
    resourceUuid = ''
  ): Observable<JsonApiResource<RoleWithRightsDto, any>> {
    switch (operation) {
      case 'new': {
        return this.getNewTemplate();
      }

      case 'duplicate': {
        return this.getNewTemplateFromOrigin(resourceUuid);
      }

      case 'edit': {
        return this.findOne(resourceUuid);
      }

      default: {
        throw new Error('Invalid operation!')
      }
    }
  }

  findRolesForNewUser(): Observable<JsonApiResource<RoleWithRightsVo[], any>> {
    return this.findAll();
  }

  getAssignedRightsMap(
    rightNames: string[],
    rolesWithRights: RoleWithRightsVo[]
  ) {
    const assignedRights = {};

    for (const rightName of rightNames) {
      assignedRights[rightName] = {};
    }

    for (const roleWithRights of rolesWithRights) {
      for (const roleRightDefinition of roleWithRights.rightDefinitions) {
        assignedRights[roleRightDefinition.rightName][roleWithRights.uuid] =
          true;
      }
    }

    return assignedRights;
  }
}
